<template>
  <v-container>
    <v-card class="mx-auto rounded-xl">
      <v-row justify="center">
        <v-col cols="12">
          <v-card class="pa-4" elevation="4">
            <v-card-title>
              {{ isEditing ? 'Editar Corretor' : 'Novo Corretor' }}
            </v-card-title>
            <v-card-text>

              <v-tabs v-model="currentTab" dark class="mb-18">
                <v-tab value="details">Detalhes</v-tab>
                <v-tab value="photos">Foto</v-tab>
              </v-tabs>

              <v-form ref="formRef" @submit.prevent="handleSubmit">
                <v-window v-model="currentTab" class="mt-2">
                  <v-window-item value="details">
                    <v-row class="mt-2">
                      <v-col cols="5">
                        <v-text-field v-model="formValue.name" label="Nome" :rules="[rules.name]"></v-text-field>
                      </v-col>
                      <v-col cols="3">
                        <v-text-field v-model="formValue.creci" label="CRECI" :rules="[rules.creci]"></v-text-field>
                      </v-col>
                      <v-col cols="4">
                        <v-text-field v-model="formValue.phone" label="Telefone" v-maska="'(##) #####-####'"
                          :rules="[rules.phone]"></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-select v-model="formValue.leadbroker_id" :items="leadBrokers" item-title="name" item-value="id"
                          label="Lider" clearable></v-select>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field v-model="formValue.email" label="E-mail" :rules="[rules.email]"></v-text-field>
                      </v-col>

                      <v-col cols="6" v-if="!isEditing">
                        <v-text-field v-model="formValue.password"
                          :label="isEditing ? 'Nova Senha (opcional)' : 'Senha'"
                          :type="showPassword ? 'text' : 'password'" :rules="passwordRules"
                          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                          @click:append="showPassword = !showPassword"></v-text-field>
                      </v-col>

                      <v-col cols="12" sm="6" md="3">
                        <v-switch v-model="formValue.is_active" color="success" label="Corretor Ativo?"></v-switch>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-switch v-model="formValue.is_plantao" color="warning" label="Está de plantão?"></v-switch>
                      </v-col>
                    </v-row>

                  </v-window-item>

                  <v-window-item value="photos">
                    <v-row>
                      <v-col cols="12">
                        <h4 class="text-h6 mb-4">Foto</h4>
                        <v-file-input label="Selecione a foto principal" accept="image/png, image/jpeg, image/jpg"
                          prepend-icon="mdi-camera" v-model="mainImage" show-size>
                        </v-file-input>

                        <div v-if="mainImagePreview" class="relative">
                          <v-img :src="mainImagePreview" height="200px" contain class="mb-4 rounded-lg" />
                          <v-icon icon="mdi-close-circle" color="red" size="28"
                            class="absolute top-2 right-2 cursor-pointer bg-white rounded-full shadow-md"
                            @click="removeMainImage" />
                        </div>
                      </v-col>
                    </v-row>
                  </v-window-item>

                </v-window>
              </v-form>
            </v-card-text>

            <v-card-actions class="d-flex justify-space-between">
              <v-btn color="secondary" variant="flat" @click="handleCancel">
                <v-icon left>mdi-arrow-left</v-icon>
                Voltar
              </v-btn>
              <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
                @click="handleSubmit">
                <v-icon left>mdi-content-save</v-icon>
                {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Corretor' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vMaska } from "maska/vue";
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingOverlay } from '@/store/loadingOverlay';

const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);

const isLoading = ref(false);
const isSubmitting = ref(false);
const isCepLoading = ref(false);
const app_url_api_storage = process.env.VUE_APP_URL_API_STORAGE;

const currentTab = ref('details');

const mainImage = ref(null);
const existingMainImageUrl = ref('');
const imagesToDelete = ref([]);

const formValue = ref({
  name: '',
  creci: '',
  phone: '',
  email: '',
  password: '',
  is_active: true,
  is_plantao: false,
});

const showPassword = ref(false);
const leadBrokers = ref([]);

const isEditing = computed(() => !!route.params.id);

const rules = {
  name: v => !!v || 'O nome é obrigatório.',
  email: v => !!v || 'O e-mail é obrigatório.',
  creci: v => !!v || 'O CRECI é obrigatório.',
  phone: v => !!v || 'O Telefone é obrigatório.',
  password: v => !!v || 'A senha é obrigatória.',
  maxImageSize: v => !v || v.every(file => file.size < 15 * 1024 * 1024) || 'Cada imagem deve ter no máximo 15MB.',
};

const passwordRules = computed(() => {
  if (isEditing.value) {
    return [];
  } else {
    return [
      v => !!v || 'A senha é obrigatória.',
      v => (v && v.length >= 6) || 'A senha deve ter no mínimo 6 caracteres.'
    ];
  }
});

const loadBroker = async (id) => {
  isLoading.value = true;
  loadingOverlay.startLoading();
  try {
    const response = await api.get(`/users/${id}`);
    const data = response.data.data;

    if (data) {
      formValue.value = {
        name: data.name,
        creci: data.creci,
        phone: data.phone,
        email: data.email,
        leadbroker_id: data.leadbroker_id,
        password: '',
        is_active: data.is_active === 1,
        is_plantao: data.is_plantao === 1,
      };

      if (data.image_url) {
        existingMainImageUrl.value = app_url_api_storage + '/images/' + data.image_url;
      } else {
        existingMainImageUrl.value = '';
      }

    } else {
      toastStore.showToast({
        message: 'O corretor não foi encontrado.',
        color: 'warning',
      });
    }

  } catch (error) {
    console.error('Erro ao carregar os dados do corretor:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar os dados do corretor',
      color: 'error',
    });
  } finally {
    isLoading.value = false;
    loadingOverlay.stopLoading();
  }
};

const mainImagePreview = computed(() => {
  if (mainImage.value) {
    return URL.createObjectURL(mainImage.value);
  }
  return existingMainImageUrl.value;
});

const removeMainImage = () => {
  if (existingMainImageUrl.value) {
    imagesToDelete.value.push(existingMainImageUrl.value.replace(app_url_api_storage + '/', ''));
  }
  existingMainImageUrl.value = '';
  mainImage.value = null;
};

const handleSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    currentTab.value = 'details';
    return;
  }

  isSubmitting.value = true;

  const formData = new FormData();

  for (const key in formValue.value) {
    const value = formValue.value[key];
    if (key === 'password' && isEditing.value && !value) {
      continue;
    }
    if (typeof value === 'boolean') {
      formData.append(key, value ? 1 : 0);
    } else if (value !== null) {
      formData.append(key, value);
    }
  }

  if (mainImage.value) {
    formData.append('mainImage', mainImage.value);
  }

  console.log('Arquivo selecionado:', mainImage.value);

  if (imagesToDelete.value.length > 0) {
    formData.append('imagesToDelete', JSON.stringify(imagesToDelete.value));
  }

  try {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    if (isEditing.value) {
      formData.append('_method', 'PUT');
      await api.post(`/brokers/${route.params.id}`, formData, config);
    } else {
      formData.append('role', 'broker');
      await api.post('/brokers', formData, config);
    }

    router.push('/app/corretores');
    toastStore.showToast({
      message: isEditing.value ? 'Corretor atualizado com sucesso!' : 'Corretor cadastrado com sucesso!',
      color: 'success',
    });

  } catch (error) {
    isSubmitting.value = false;
    console.error('Erro ao salvar corretor:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao salvar corretor',
      color: 'error',
    });
  }
};

const handleCancel = () => {
  router.push('/app/corretores');
};

const loadLeadBrokers = async () => {
  isLoading.value = true;
  try {
    const response = await api.get('/brokers', {
      params: {
        role: ['leadbroker']
      }
    });

    leadBrokers.value = response.data.data;
  } catch (error) {
    console.error('Erro ao carregar corretores:', error);
    hasError.value = true;
    toastStore.showToast({
      message: error?.response?.data?.error || 'Ocorreu um erro ao carregar a lista de corretores.',
      color: 'error',
    });
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadLeadBrokers();
  if (isEditing.value) {
    loadBroker(route.params.id);
  }
});

</script>